import { useState, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  InputAdornment,
} from "@mui/material";
import {
  useGetClubsQuery,
  useCreateClubMutation,
  useUpdateClubMutation,
  useDeleteClubMutation,
  useUploadClubLogoMutation,
  useDeleteClubLogoMutation,
  Club,
} from "../../../entities/admin-clubs";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import UploadIcon from "@mui/icons-material/Upload";
import GroupsIcon from "@mui/icons-material/Groups";
import {
  FILE_SIZE_LIMIT_BYTES,
  FILE_SIZE_LIMIT_LABEL,
  getFileSizeLimitError,
} from "../../../shared/constants/file-upload-limits";

export const ClubsManagement = () => {
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [clubName, setClubName] = useState("");
  const [clubDescription, setClubDescription] = useState("");

  const { data: clubsData, isLoading: isLoadingClubs } = useGetClubsQuery();

  const [createClub, { isLoading: isCreating }] = useCreateClubMutation();
  const [updateClub, { isLoading: isUpdating }] = useUpdateClubMutation();
  const [deleteClub, { isLoading: isDeleting }] = useDeleteClubMutation();
  const [uploadClubLogo, { isLoading: isUploadingLogo }] =
    useUploadClubLogoMutation();
  const [deleteClubLogo] = useDeleteClubLogoMutation();

  const filteredClubs = useMemo(() => {
    if (!clubsData?.clubs) return [];
    return clubsData.clubs.filter((club) =>
      club.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [clubsData?.clubs, searchQuery]);

  const handleOpenCreateDialog = () => {
    setClubName("");
    setClubDescription("");
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (club: Club) => {
    setSelectedClub(club);
    setClubName(club.name ?? "");
    setClubDescription(club.description ?? "");
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (club: Club) => {
    setSelectedClub(club);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setSelectedClub(null);
  };

  const isFormValid = () => clubName.trim() !== "" && clubDescription.trim() !== "";

  const handleCreateClub = async () => {
    if (!isFormValid()) return;
    try {
      await createClub({
        name: clubName.trim(),
        description: clubDescription.trim(),
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error creating club:", error);
    }
  };

  const handleUpdateClub = async () => {
    if (!selectedClub || !isFormValid()) return;
    try {
      await updateClub({
        id: selectedClub.id,
        body: {
          name: clubName.trim(),
          description: clubDescription.trim(),
        },
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error updating club:", error);
    }
  };

  const handleDeleteClub = async () => {
    if (!selectedClub) return;
    try {
      await deleteClub(selectedClub.id).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error deleting club:", error);
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!selectedClub || !event.target.files || event.target.files.length === 0)
      return;

    const file = event.target.files[0];
    const sizeError = getFileSizeLimitError(file, FILE_SIZE_LIMIT_BYTES.DEFAULT);
    if (sizeError) {
      alert(sizeError);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const result = await uploadClubLogo({
        id: selectedClub.id,
        file: formData,
      }).unwrap();
      setSelectedClub({ ...selectedClub, logoUrl: result.url });
    } catch (error) {
      console.error("Error uploading club logo:", error);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteLogo = async () => {
    if (!selectedClub) return;
    try {
      await deleteClubLogo(selectedClub.id).unwrap();
      setSelectedClub({ ...selectedClub, logoUrl: undefined });
    } catch (error) {
      console.error("Error deleting club logo:", error);
    }
  };

  const renderClubForm = (withLogo: boolean) => (
    <Grid container spacing={2}>
      {withLogo && selectedClub && (
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              mb: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Avatar
              src={selectedClub.logoUrl}
              variant="rounded"
              imgProps={{ style: { objectFit: "contain" } }}
              sx={{ width: 72, height: 72, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}
            >
              <GroupsIcon color="disabled" />
            </Avatar>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingLogo}
                >
                  {isUploadingLogo ? <CircularProgress size={20} /> : "Загрузить логотип"}
                </Button>
                {selectedClub.logoUrl && (
                  <Button variant="outlined" size="small" color="error" onClick={handleDeleteLogo}>
                    Удалить
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                PNG или JPG, квадратное изображение, до {FILE_SIZE_LIMIT_LABEL.DEFAULT}
              </Typography>
            </Box>
          </Box>
        </Grid>
      )}
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Название"
          fullWidth
          value={clubName}
          onChange={(e) => setClubName(e.target.value)}
          margin="normal"
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Описание"
          fullWidth
          multiline
          minRows={3}
          value={clubDescription}
          onChange={(e) => setClubDescription(e.target.value)}
          margin="normal"
        />
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Клубы коллаборации
      </Typography>

      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <Paper sx={{ width: "100%", p: 2, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography fontWeight={600} fontSize={18}>
              Список клубов
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
              Добавить
            </Button>
          </Box>

          <TextField
            placeholder="Поиск клуба"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {isLoadingClubs ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredClubs.length ? (
            <Box sx={{ overflow: "auto", flexGrow: 1, height: 0 }}>
              <List sx={{ padding: 0 }}>
                {filteredClubs.map((club) => (
                  <ListItem
                    key={club.id}
                    sx={{ borderRadius: 1, mb: 1 }}
                    secondaryAction={
                      <Box>
                        <IconButton
                          onClick={() => handleOpenEditDialog(club)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleOpenDeleteDialog(club)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={club.logoUrl}>
                        <GroupsIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={club.name} secondary={club.description} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Typography color="text.secondary" align="center" py={3}>
              Клубы не найдены
            </Typography>
          )}
        </Paper>
      </Box>

      <Dialog open={openCreateDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить клуб</DialogTitle>
        <DialogContent>{renderClubForm(false)}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Отмена</Button>
          <Button onClick={handleCreateClub} disabled={!isFormValid() || isCreating}>
            {isCreating ? <CircularProgress size={24} /> : "Создать"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Редактировать клуб</DialogTitle>
        <DialogContent>{renderClubForm(true)}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Отмена</Button>
          <Button onClick={handleUpdateClub} disabled={!isFormValid() || isUpdating}>
            {isUpdating ? <CircularProgress size={24} /> : "Сохранить"}
          </Button>
        </DialogActions>
      </Dialog>

      {openDeleteDialog && selectedClub && (
        <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
          <DialogTitle>Удалить клуб</DialogTitle>
          <DialogContent>
            <Typography>
              Вы уверены, что хотите удалить клуб "{selectedClub.name}"? Связи с
              резидентами будут сняты.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Отмена</Button>
            <Button color="error" onClick={handleDeleteClub} disabled={isDeleting}>
              {isDeleting ? <CircularProgress size={24} /> : "Удалить"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
